import {useMinSpin} from "../../hooks/useMinSpin";
import "./GiveawaysPage.scss";
import 'rsuite/useToaster/styles/index.css';
import 'rsuite/Message/styles/index.css';
import {CSSProperties, useCallback, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import {PATHS} from "../../routers/utils/Paths.links";
import {useI18n} from "../../i18n/I18nProvider";
import type {TranslationKey} from "../../i18n/ua";
import {useUserStore} from "../../../modules/shop/user/store";
import ProductImage from "../../shared/ProductImage/ProductImage";
import ButtonLabel from "../../shared/ButtonLoader/ButtonLoader";
import Confetti from "../../shared/Confetti/Confetti";
import Toaster, {ToasterProps} from "../../shared/Toaster/Toaster";
import {getCurrentDateInSeconds} from "../../shared/functions/getRandomId";
import {formatDate} from "../../shared/functions/formateDate";
import {CheckIcon, GiftIcon, LockIcon, TicketIcon, TypeIcon} from "./GiveawaysArt";
import FloatingPrizes from "../../shared/FloatingPrizes/FloatingPrizes";

type GiveawayType = "all" | "priority" | "set";

interface IServer {
    _id: string,
    name: string,
}

interface IPrize {
    name: string,
    imageUrl: string,
    price: number,
    type: string,
}

interface IPerson {
    personaName: string,
    avatarUrl: string,
}

interface IGiveaway {
    _id: string,
    type: GiveawayType,
    status: string,
    prize: IPrize,
    servers: IServer[],
    endsAt: string,
    participantsCount: number,
    recentParticipants: IPerson[],
}

interface IWinner {
    _id: string,
    type: GiveawayType,
    prize: IPrize,
    participantsCount: number,
    finishedAt: string,
    winner: IPerson,
}

interface IPage {
    rules: { minPrizePrice: number },
    active: IGiveaway[],
    winners: IWinner[],
}

interface IMe {
    joined: Record<string, string>,
    hasPriority: boolean,
    hasSet: boolean,
}

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

const pad = (n: number) => String(n).padStart(2, "0");

// годинник, що оновлюється щосекунди
const useNow = () => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    return now;
};

const Avatar = ({ person, className = "" }: { person: IPerson, className?: string }) => (
    person.avatarUrl
        ? <img className={`gw-avatar ${className}`} src={person.avatarUrl} alt={person.personaName} title={person.personaName} />
        : <span className={`gw-avatar gw-avatar-empty ${className}`} title={person.personaName}>{person.personaName?.[0]?.toUpperCase() ?? "?"}</span>
);

const Countdown = ({ endsAt, t }: { endsAt: string, t: TFn }) => {
    const now = useNow();
    const left = Math.max(0, new Date(endsAt).getTime() - now);

    if (left === 0) {
        return <div className="gw-countdown gw-countdown-done">{t("giveaways.drawing")}</div>;
    }

    const seconds = Math.floor(left / 1000);
    const parts = [
        { value: Math.floor(seconds / 86400), label: t("giveaways.days") },
        { value: Math.floor(seconds / 3600) % 24, label: t("stats.hours") },
        { value: Math.floor(seconds / 60) % 60, label: t("stats.minutes") },
        { value: seconds % 60, label: t("giveaways.seconds") },
    ];

    return (
        <div className="gw-countdown" role="timer">
            { parts.map((p, i) =>
                <div key={i} className="gw-countdown-cell">
                    {/* key змінюється разом зі значенням — цифра «перегортається» анімацією */}
                    <span key={p.value} className="gw-countdown-value">{pad(p.value)}</span>
                    <span className="gw-countdown-label">{p.label}</span>
                </div>
            )}
        </div>
    );
};

const GiveawayCard = ({ giveaway, me, isLoggedIn, t, onJoined, onError }: {
    giveaway: IGiveaway,
    me: IMe | null,
    isLoggedIn: boolean,
    t: TFn,
    onJoined: (id: string, server: string, participantsCount: number) => void,
    onError: (message: string) => void,
}) => {
    const [serverId, setServerId] = useState(giveaway.servers[0]?._id);
    const [loading, setLoading] = useState(false);
    // спінер видно щонайменше пів секунди (useMinSpin)
    const joinSpin = useMinSpin(loading);
    const [burst, setBurst] = useState(0);
    const now = useNow();

    const joinedServer = me?.joined[giveaway._id];
    const finished = giveaway.status !== "active" || new Date(giveaway.endsAt).getTime() <= now;
    const locked = !!me && ((giveaway.type === "priority" && !me.hasPriority) || (giveaway.type === "set" && !me.hasSet));

    const join = async () => {
        if (loading || !serverId) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${PATHS_API.GIVEAWAYS}/${giveaway._id}/join`, { serverId }, { withCredentials: true });
            onJoined(giveaway._id, data.server, data.participantsCount);
            setBurst((b) => b + 1);
        } catch (e: any) {
            const message = e.response?.data?.message;
            onError(typeof message === "string" ? message : t("giveaways.joinError"));
        } finally {
            setLoading(false);
        }
    };

    const rule = t(`giveaways.rule.${giveaway.type}` as TranslationKey);

    return (
        <div className={`gw-card gw-type-${giveaway.type} ${joinedServer ? "gw-card-joined" : ""}`}>
            { burst > 0 && <Confetti key={burst} count={36} className="gw-card-confetti" /> }

            <div className="gw-card-head">
                <span className="gw-badge"><TypeIcon type={giveaway.type} />{t(`giveaways.type.${giveaway.type}` as TranslationKey)}</span>
            </div>

            <div className="gw-prize">
                <div className="gw-prize-glow" />
                <ProductImage className="gw-prize-img" src={giveaway.prize.imageUrl} alt={giveaway.prize.name} />
            </div>
            <div className="gw-prize-name" title={giveaway.prize.name}>{giveaway.prize.name}</div>
            <div className="gw-prize-worth">{t("giveaways.worth", { price: giveaway.prize.price })}</div>
            <p className="gw-rule">{rule}</p>

            <div className="gw-participants">
                { giveaway.recentParticipants.length > 0
                    ? <>
                        <div className="gw-avatars">
                            { giveaway.recentParticipants.slice(0, 5).map((p, i) =>
                                <Avatar key={i} person={p} className="gw-avatar-stack" />
                            )}
                        </div>
                        <span>{t("giveaways.participants", { n: giveaway.participantsCount })}</span>
                    </>
                    : <span className="gw-muted">{t("giveaways.beFirst")}</span>
                }
            </div>

            <div className="gw-card-actions">
                { !isLoggedIn && !finished &&
                    <a className="gw-join-btn" href={PATHS_API.LOGIN}>{t("giveaways.login")}</a>
                }

                { isLoggedIn && joinedServer &&
                    <div className="gw-joined"><CheckIcon />{t("giveaways.joined", { server: joinedServer })}</div>
                }

                { isLoggedIn && !joinedServer && !finished && locked &&
                    <div className="gw-locked">
                        <span><LockIcon />{t(giveaway.type === "priority" ? "giveaways.needPriority" : "giveaways.needSet")}</span>
                        <Link className="gw-ghost-btn" to={PATHS.SHOP}>{t("giveaways.toShop")}</Link>
                    </div>
                }

                { isLoggedIn && !joinedServer && !finished && !locked &&
                    <>
                        { giveaway.servers.length > 1 &&
                            <div className="gw-servers" role="radiogroup" aria-label={t("giveaways.server")}>
                                { giveaway.servers.map((s) =>
                                    <button
                                        key={s._id}
                                        type="button"
                                        role="radio"
                                        aria-checked={serverId === s._id}
                                        className={`gw-server ${serverId === s._id ? "gw-server-active" : ""}`}
                                        onClick={() => setServerId(s._id)}
                                    >
                                        {s.name}
                                    </button>
                                )}
                            </div>
                        }
                        <button type="button" className={`gw-join-btn ${joinSpin ? "btn-loading" : ""}`} onClick={join} disabled={joinSpin}>
                            <ButtonLabel loading={joinSpin} loadingText={t("giveaways.checking")}>
                                <TicketIcon />{t("giveaways.join")}
                            </ButtonLabel>
                        </button>
                    </>
                }

                { finished && !joinedServer && <div className="gw-muted gw-center">{t("giveaways.drawing")}</div> }
            </div>
        </div>
    );
};

const GiveawaysSkeleton = () => (
    <div className="gw-grid">
        { [0, 1, 2].map((i) => <div key={i} className="gw-card gw-skel" />) }
    </div>
);

const GiveawaysPage = () => {
    const { t } = useI18n();
    const user = useUserStore((s) => s.user);
    const isLoggedIn = !!user?.steamId;

    const [page, setPage] = useState<IPage | null>(null);
    const [me, setMe] = useState<IMe | null>(null);
    const [error, setError] = useState(false);
    const [toastProps, setToastProps] = useState<ToasterProps | null>(null);

    const load = useCallback(() => {
        axios.get(PATHS_API.GIVEAWAYS)
            .then(({ data }) => { setPage(data); setError(false); })
            .catch((e) => {
                console.error("Помилка завантаження розіграшів:", e);
                setError(true);
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isLoggedIn) { setMe(null); return; }
        axios.get(PATHS_API.GIVEAWAYS_ME, { withCredentials: true })
            .then(({ data }) => setMe(data))
            .catch((e) => console.error("Помилка завантаження стану розіграшів:", e));
    }, [isLoggedIn]);

    // коли час вийшов — підтягуємо підсумки й нові розіграші, поки сервер їх не підведе
    const endsAt = page?.active[0]?.endsAt;
    const now = useNow();
    const awaitingDraw = !!page && (page.active.length < 3 || page.active.some((g) => g.status !== "active" || new Date(g.endsAt).getTime() <= now));
    useEffect(() => {
        if (!awaitingDraw) return;
        const timer = window.setInterval(load, 10_000);
        return () => window.clearInterval(timer);
    }, [awaitingDraw, load]);

    const onJoined = (id: string, server: string, participantsCount: number) => {
        setMe((m) => m && ({ ...m, joined: { ...m.joined, [id]: server } }));
        setPage((p) => p && ({
            ...p,
            active: p.active.map((g) => g._id === id
                ? { ...g, participantsCount, recentParticipants: [{ personaName: user.personaName, avatarUrl: user.avatarUrl }, ...g.recentParticipants] }
                : g),
        }));
        setToastProps({ message: t("giveaways.joinedToast"), type: "success", id: getCurrentDateInSeconds() + id });
    };

    const onError = (message: string) => setToastProps({ message, type: "error", id: getCurrentDateInSeconds() + message });

    return (
        <div className="containerContent gw-page">
            <div className="gw-hero">
                <div>
                    <h3 className="gw-title">{t("giveaways.title")}</h3>
                    <p className="gw-subtitle">{t("giveaways.subtitle")}</p>
                </div>
                {/* замість намальованого подарунка — справжні призи поточних розіграшів, найдорожчий по центру */}
                <FloatingPrizes
                    className="gw-hero-prizes"
                    images={[...(page?.active ?? [])]
                        .filter((g) => g.prize?.imageUrl)
                        .sort((a, b) => (b.prize.price ?? 0) - (a.prize.price ?? 0))
                        .map((g) => g.prize.imageUrl)}
                />
            </div>

            { endsAt &&
                <div className="gw-timer">
                    <div className="gw-timer-text">
                        <span className="gw-timer-title"><GiftIcon />{t("giveaways.drawIn")}</span>
                        <span className="gw-muted">{t("giveaways.drawAt")}</span>
                    </div>
                    <Countdown endsAt={endsAt} t={t} />
                </div>
            }

            { error && <div className="gw-message">{t("giveaways.error")}</div> }
            { !page && !error && <GiveawaysSkeleton /> }
            { page && page.active.length === 0 && <div className="gw-message">{t("giveaways.empty")}</div> }

            { page && page.active.length > 0 &&
                <div className="gw-grid">
                    { page.active.map((g, i) =>
                        <div key={g._id} className="gw-card-wrap" style={{ "--i": i } as CSSProperties}>
                            <GiveawayCard
                                giveaway={g}
                                me={me}
                                isLoggedIn={isLoggedIn}
                                t={t}
                                onJoined={onJoined}
                                onError={onError}
                            />
                        </div>
                    )}
                </div>
            }

            <h4 className="gw-section-title">{t("giveaways.winners")}</h4>
            { page && page.winners.length === 0 && <div className="gw-message">{t("giveaways.noWinners")}</div> }
            { page && page.winners.length > 0 &&
                <div className="gw-winners">
                    { page.winners.map((w, i) =>
                        <div key={w._id} className={`gw-winner gw-type-${w.type}`} style={{ "--i": i } as CSSProperties}>
                            <Avatar person={w.winner} className="gw-winner-avatar" />
                            <div className="gw-winner-info">
                                <div className="gw-winner-name" title={w.winner.personaName}>{w.winner.personaName}</div>
                                <div className="gw-winner-meta">
                                    <TypeIcon type={w.type} />
                                    <span>{formatDate(w.finishedAt)}</span>
                                    <span>· {t("giveaways.of", { n: w.participantsCount })}</span>
                                </div>
                            </div>
                            <div className="gw-winner-prize" title={w.prize.name}>
                                <ProductImage src={w.prize.imageUrl} alt={w.prize.name} />
                            </div>
                        </div>
                    )}
                </div>
            }

            {toastProps && <Toaster message={toastProps.message} type={toastProps.type} id={toastProps.id} />}
        </div>
    );
};

export default GiveawaysPage;
